/**
 * Error Fallback UI
 *
 * User-friendly error screen shown when ErrorBoundary catches an error.
 * Provides actionable next steps instead of showing raw error messages.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCw } from "lucide-react";

interface ErrorFallbackProps {
  error: Error | null;
  onReset?: () => void;
}

export function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV;

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-destructive/10 rounded-full">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <div>
              <CardTitle className="text-2xl">Something Went Wrong</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                We encountered an unexpected error
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* User-friendly message */}
          <div className="p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">What happened?</h3>
            <p className="text-sm text-muted-foreground">
              The application encountered an unexpected problem and couldn't continue.
              This has been logged and we'll work to prevent it in the future.
            </p>
          </div>

          {/* What to do next */}
          <div>
            <h3 className="font-semibold mb-3">What can you do?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-medium">1.</span>
                <span>Try refreshing the page - this often resolves temporary issues</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-medium">2.</span>
                <span>Return to the home page and try again</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-medium">3.</span>
                <span>If the problem persists, your local data is safe - just reload</span>
              </li>
            </ul>
          </div>

          {/* Technical details (dev only) */}
          {isDev && error && (
            <details className="p-4 bg-destructive/10 rounded-lg">
              <summary className="cursor-pointer font-semibold text-destructive mb-2">
                Technical Details (Development Only)
              </summary>
              <div className="mt-2 space-y-2">
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Error Message:
                  </div>
                  <pre className="text-xs bg-background p-2 rounded overflow-x-auto">
                    {error.message}
                  </pre>
                </div>
                {error.stack && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-1">
                      Stack Trace:
                    </div>
                    <pre className="text-xs bg-background p-2 rounded overflow-x-auto max-h-48 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              size="lg"
              onClick={handleReload}
              className="flex-1"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload Page
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleGoHome}
              className="flex-1"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Home
            </Button>
          </div>

          {/* Optional: Try to recover without reload */}
          {onReset && (
            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                onClick={onReset}
                className="w-full"
              >
                Try to Recover (Experimental)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
